import React, { FC, useEffect } from 'react';
import {
  Box,
  Col,
  Glyph,
  FlexRow,
  Body,
} from '@edreamsodigeo/prisma-design-system';
import {
  useTranslation,
  Show,
  useTracking,
} from '@edreamsodigeo/frontend-core';
import { FlexibleDatesRepricingMessagePositionType } from './FlexibleDatesRepricingMessageDecorator';
import { FlexibleDatesRepricingTrendType } from 'clientState/flexibleDatesDealHelper';
import { RESULTS_CATEGORY } from 'clientState/providers/TrackingProviders';
import { useDisplayedItinerariesQuery } from 'clientState/providers/DisplayedItinerariesProvider';
import { useUrlParams } from 'clientState/hooks/useUrlParams';

export type FlexibleDatesRepricingProps = {
  trend: FlexibleDatesRepricingTrendType;
  position: FlexibleDatesRepricingMessagePositionType;
};

type FlexibleDatesRepricingMessageProps = {
  position?: FlexibleDatesRepricingMessagePositionType;
  trend?: FlexibleDatesRepricingTrendType;
  sx?: Record<string, unknown>;
  mainText?: string;
  secondaryText?: string;
};

const FlexibleDatesRepricingMessageWrapperInsideCard: FC<
  FlexibleDatesRepricingMessageProps
> = ({ sx, children }) => (
  <Box p={'1x_small'} sx={sx} borderRadius="regular">
    {children}
  </Box>
);

const FlexibleDatesRepricingMessageWrapperBelowCard: FC<
  FlexibleDatesRepricingMessageProps
> = ({ sx, children }) => (
  <Box p="1x_small" sx={sx} borderRadius="regular" pt="2x_big">
    {children}
  </Box>
);

const FlexibleDatesRepricingMessageWraper: FC<
  FlexibleDatesRepricingMessageProps
> = ({ trend, position, children }) => {
  const sx =
    trend === FlexibleDatesRepricingTrendType.down
      ? {
          backgroundColor: 'green.20',
        }
      : {
          background:
            'linear-gradient(90deg, rgba(249, 112, 12, 0.2) 38.49%, rgba(218, 56, 53, 0.2) 96.35%)',
        };
  return (
    <>
      <Show
        when={position === FlexibleDatesRepricingMessagePositionType.insideCard}
      >
        <FlexibleDatesRepricingMessageWrapperInsideCard sx={sx}>
          {children}
        </FlexibleDatesRepricingMessageWrapperInsideCard>
      </Show>
      <Show
        when={position === FlexibleDatesRepricingMessagePositionType.belowCard}
      >
        <FlexibleDatesRepricingMessageWrapperBelowCard sx={sx}>
          {children}
        </FlexibleDatesRepricingMessageWrapperBelowCard>
      </Show>
    </>
  );
};

const FlexibleDatesRepricingMessage: FC<FlexibleDatesRepricingMessageProps> = ({
  trend,
  position,
}) => {
  const { t } = useTranslation();
  const title =
    trend === FlexibleDatesRepricingTrendType.up
      ? t('flexibleDates.reprice.high.title')
      : t('flexibleDates.reprice.low.title');

  const subTitle =
    trend === FlexibleDatesRepricingTrendType.up
      ? t('flexibleDates.reprice.high.subtitle')
      : t('flexibleDates.reprice.low.subtitle');

  const textColor =
    trend === FlexibleDatesRepricingTrendType.down ? 'green.50' : 'red.50';

  const bodyType =
    position === FlexibleDatesRepricingMessagePositionType.belowCard
      ? 'small'
      : 'xSmall';
  return (
    <FlexRow>
      <Col>
        <Box pt="tiny">
          <TrendIcon trend={trend} />
        </Box>
      </Col>
      <Col>
        <Box ml="1x_small">
          <Body
            as="span"
            weight="medium"
            type={bodyType}
            color={textColor}
            inline
          >
            {title}
          </Body>{' '}
          <Body as="span" type={bodyType} color={textColor} inline>
            {subTitle}
          </Body>
        </Box>
      </Col>
    </FlexRow>
  );
};

const TrendIcon: FC<FlexibleDatesRepricingMessageProps> = ({ trend }) => (
  <>
    <Show when={trend === FlexibleDatesRepricingTrendType.up}>
      <Glyph
        category="Deals"
        name="TrendingUp"
        color={'red.50'}
        size={'small'}
      />
    </Show>
    <Show when={trend === FlexibleDatesRepricingTrendType.down}>
      <Glyph
        category="Deals"
        name="TrendingUp"
        color={'green.50'}
        size={'small'}
        sx={{ transform: 'scaleY(-1)' }}
      />
    </Show>
  </>
);

const FlexibleDatesRepricing: FC<FlexibleDatesRepricingProps> = ({
  trend,
  position,
}) => {
  const { data } = useDisplayedItinerariesQuery();
  const { trackEvent } = useTracking();
  const { fdp, bd } = useUrlParams(window.location.href);
  const cheapestPrice = data?.cheapestItineraryPrice;
  const priceDifference = (cheapestPrice - Number(fdp)).toFixed(2);

  useEffect(() => {
    trackEvent({
      category: RESULTS_CATEGORY,
      action: 'flex_days',
      label: `flex_days_accuracy_RT_${
        bd === 'true' ? 'deal' : 'gen'
      }_pf:${fdp}_pr:${cheapestPrice}_pd:${priceDifference}_pag:flexcalendar`,
    });
  });
  return (
    <Show when={trend !== FlexibleDatesRepricingTrendType.equal}>
      <FlexibleDatesRepricingMessageWraper trend={trend} position={position}>
        <FlexibleDatesRepricingMessage trend={trend} position={position} />
      </FlexibleDatesRepricingMessageWraper>
    </Show>
  );
};

export default FlexibleDatesRepricing;
